// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { ProfileBaseService } from './profile-base.service';
import {
  UserProfileModel,
  ApplicationError,
  SettingsModel,
  UserDetailsModel,
  ToastModel,
  EmailAddressValidationModel
} from '../../../models';
import { environment } from '../../../../environments/environment';
import { AppService } from '../app.service';
import { AppToastService } from '../app-toast.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class ProfileService extends ProfileBaseService {
  constructor(
    private http: HttpClient,
    protected appService: AppService,
    private translate: TranslateService,
    private toastService: AppToastService
  ) {
    super();
  }

  loadProfile(): void {
    if (this._isLoaded) {
      return;
    }

    let url = environment.apiConfig.baseUrl + environment.apiConfig.profileApi;

    this.http
      .get<UserDetailsModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(result => {
        this._userDetails$.next(result);
      });

    url = environment.apiConfig.baseUrl + environment.apiConfig.settingsApi;

    this.http
      .get<SettingsModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._settingsError.bind(this))
      )
      .subscribe(settings => {
        this._settings$.next(settings);
      });

    this._isLoaded = true;
  }

  update(userProfile: UserProfileModel): Observable<boolean> {
    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.profileApi;

    return this.http
      .put<UserProfileModel>(url, { userDetails: userProfile })
      .pipe(
        retryBackoff(environment.retryConfig),
        tap(result => this._userDetails$.next(null)),
        catchError(this._updateError.bind(this))
      );
  }

  updateUserDetails(userDetails: UserDetailsModel): void {
    if (!userDetails) {
      return;
    }

    userDetails.emailAddress = this._userDetails$.value.emailAddress;

    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.profileApi;
    this._isUpdating$.next(true);
    this.http
      .put<UserDetailsModel>(url, userDetails)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._updateError.bind(this))
      )
      .subscribe(ud => {
        if (ud.hasUpdateError) {
          this._updateUserDetailsError$.next(ud.updateErrorMessage);
        } else {
          this.toastService.show({
            header: this.translate.instant('updateSuccessHeader'),
            body: this.translate.instant('updateUserDetailsSuccessBody'),
            class: 'bg-success text-light'
          } as ToastModel);
        }
        this._userDetails$.next(ud);
        this._updateUserDetailsResult$.next(!ud.hasUpdateError);
        this._isUpdating$.next(false);
      });
  }

  updateLoginDetails(loginDetails: UserDetailsModel): void {
    if (!loginDetails) {
      return;
    }

    loginDetails.firstName = this._userDetails$.value.firstName;
    loginDetails.lastName = this._userDetails$.value.lastName;

    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.profileApi;
    this._isUpdating$.next(true);
    this.http
      .put<UserDetailsModel>(url, loginDetails)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._updateError.bind(this))
      )
      .subscribe(ud => {
        if (ud.hasUpdateError) {
          this._updateLoginDetailsError$.next(ud.updateErrorMessage);
        } else {
          this.toastService.show({
            header: this.translate.instant('updateSuccessHeader'),
            body: this.translate.instant('updateLoginDetailsSuccessBody'),
            class: 'bg-success text-light'
          } as ToastModel);
        }
        this._userDetails$.next(ud);
        this._updateLoginDetailsResult$.next(!ud.hasUpdateError);
        this._isUpdating$.next(false);
      });
  }

  checkIfEmailAddressInUse(emailAddress: string): void {
    if (!emailAddress) {
      return;
    }
    const uri =
      environment.apiConfig.baseUrl +
      environment.apiConfig.checkEmailAddressApi +
      `?emailAddress=${encodeURIComponent(emailAddress)}`;
    this._isValidatingEmailAddress$.next(true);

    this.http
      .get<EmailAddressValidationModel>(uri)
      .pipe(catchError(this._emailError.bind(this)))
      .subscribe((result: EmailAddressValidationModel) => {
        this._isEmailAddressInUse$.next(result);
        this._isValidatingEmailAddress$.next(false);
      });
  }

  private _modelError(err, caught) {
    this._handleError(err);
    return of(null as UserProfileModel);
  }

  private _settingsError(err, caught) {
    this._handleError(err);
    return of(null as SettingsModel);
  }

  private _updateError(err, caught) {
    this._handleError(err);
    return of(false);
  }

  private _emailError(err, caught) {
    this._isValidatingEmailAddress$.next(false);
    this._handleError(err);
  }

  private _handleError(err) {
    this.appService.setLoading(false);
    this._isUpdating$.next(false);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        errorNumber: err.status,
        detailedMessage
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);
  }
}
